import { Circle, Navigation, Store } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../app/store";
import { Col } from "../../../components/col";
import { Row } from "../../../components/row";
import HeatMapPrintButton from "../../heat-map/components/map/shared/HeatMapPrintButton";
import MerchantHeatMap from "./components/map/MerchantHeatMap";
import { NeighborhoodTable } from "./components/NeighborhoodTable";
import { REDEMPTION_LEVEL_COLORS } from "./MerchantHeatmap.const";
import { fetchMerchantHeatmapData } from "./MerchantHeatmap.slice";

export default function MerchantHeatmapPdfReport() {
    return <MerchantHeatmapPdfReportContent />;
}

function MerchantHeatmapPdfReportContent() {
    const dispatch = useDispatch<AppDispatch>();
    const { data } = useSelector((state: RootState) => state.merchantHeatmap);
    const [pageReady, setPageReady] = useState<boolean>(false);

    useEffect(() => {
        if (!data) {
            dispatch(fetchMerchantHeatmapData(undefined));
        }
    }, [dispatch, data]);

    useEffect(() => {
        if (data) {
            setPageReady(true);
        }
    }, [data]);

    if (!data) {
        return (
            <div className="w-full h-[800px] flex items-center justify-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
            </div>
        );
    }

    return (
        <>
            {!pageReady && (
                <div className="w-full h-[800px] flex items-center justify-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
                </div>
            )}
            {pageReady && (
                <HeatMapPrintButton autoPrint={pageReady}>
                    {/* Header Section */}
                    <Row className="w-full flex items-center justify-center">
                        <div className="w-[750px]">
                            <div className="flex justify-between pb-5">
                                <Col className="gap-0">
                                    <span className="font-bold text-lg">Coupon Redemption Analytics Report</span>
                                    <span className="text-xs text-[#4A5565] flex gap-1 items-center">
                                        City of Fairburn
                                        <Circle size={4} stroke="#4A5565" fill="#4A5565"></Circle>
                                        {(new Date()).toLocaleString("en-US", { month: "long", year: "numeric" })}
                                    </span>
                                </Col>
                            </div>
                            {/* Metric Cards Summary */}
                            <Row className="py-2 bg-[#F9FAFB] text-[10px]">
                                <Col className="w-1/4 gap-0">
                                    <span className="text-center text-[12px] font-bold">
                                        {data.active_coupons}/{data.active_coupons_limit}
                                    </span>
                                    <span className="text-center text-[#6A7282]">Active Coupons</span>
                                </Col>
                                <Col className="w-1/4 gap-0">
                                    <span className="text-center text-[12px] font-bold">
                                        {Math.round(data.conversion_rate)}%
                                    </span>
                                    <span className="text-center text-[#6A7282]">Overall Conversion Rate</span>
                                    <span className="text-center text-[#6A7282]">
                                        {data.total_views} views → {data.total_redemptions} redemptions
                                    </span>
                                </Col>
                                <Col className="w-1/4 gap-0">
                                    <span className="text-center text-[12px] font-bold">
                                        {data.best_performing.coupon_name}
                                    </span>
                                    <span className="text-center text-[#6A7282]">Best Performing</span>
                                    <span className="text-center text-[#6A7282]">
                                        {Math.round(data.best_performing.conversion_rate)}% conversion • {data.best_performing.redemptions} redemptions
                                    </span>
                                </Col>
                                <Col className="w-1/4 gap-0">
                                    <span className="text-center text-[12px] font-bold">
                                        {data.redemption_breakdown.in_app}/{data.redemption_breakdown.in_store}
                                    </span>
                                    <span className="text-center text-[#6A7282]">Redemption Breakdown</span>
                                    <span className="text-center text-[#6A7282]">In-App / In-store</span>
                                </Col>
                            </Row>
                            <hr className="mt-2 mb-3 border-[#EBEBEB]"></hr>
                        </div>
                    </Row>

                    {/* Heat Map Section */}
                    <div className="h-[700px] mx-[40px] my-[10px] border border-gray-200 dark:border-gray-700 rounded-2xl">
                        <Row className="px-5 pt-5 pb-5 justify-between items-center">
                            <Col className="gap-1 text-sm">
                                <div className="flex">
                                    <Store className="mr-1" color="#00C49A" size={16} />
                                    <span className="text-gray-900 dark:text-gray-100">Coupon Redemption Heat Map</span>
                                </div>
                                <div className="text-gray-600 dark:text-gray-400">
                                    Interactive geographic distribution of coupon redemptions across city districts
                                </div>
                            </Col>
                        </Row>
                        <Row className="h-[600px] flex justify-center items-stretch">
                            <div className="w-[750px] bg-white dark:bg-gray-800 flex flex-col">
                                <div className="relative rounded-2xl flex-1">
                                    {/* Redemption Levels legend */}
                                    <div className="absolute top-6 right-6 bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-600 rounded-2xl px-4 py-3 text-sm z-[9999]">
                                        <div className="font-semibold mb-2 flex items-center text-[#00BCD4] dark:text-cyan-400 text-[12px]">
                                            <Navigation size={16} className="mr-1" />
                                            <span>Redemption Levels</span>
                                        </div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: REDEMPTION_LEVEL_COLORS.high }}
                                            ></span>
                                            <span className="text-[12px] text-gray-700 dark:text-gray-300">
                                                High (&gt;70%)
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: REDEMPTION_LEVEL_COLORS.medium }}
                                            ></span>
                                            <span className="text-[12px] text-gray-700 dark:text-gray-300">
                                                Medium (50%-70%)
                                            </span>
                                        </div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span
                                                className="w-3 h-3 rounded-full"
                                                style={{ backgroundColor: REDEMPTION_LEVEL_COLORS.low }}
                                            ></span>
                                            <span className="text-[12px] text-gray-700 dark:text-gray-300">
                                                Low (&lt;50%)
                                            </span>
                                        </div>
                                        <hr className="my-2 border-gray-200 dark:border-gray-600" />
                                        <span className="text-[12px] text-gray-500 dark:text-gray-400">
                                            Click markers for details
                                        </span>
                                    </div>

                                    {/* City Info */}
                                    <div
                                        className="absolute bottom-6 left-6 bg-white dark:bg-gray-800 shadow-md border-2 border-gray-200 dark:border-gray-600 rounded-xl px-2 py-1 text-[10px] z-[9999]">
                                        <div className="flex justify-left items-center">
                                            <Circle className="mr-2" size={12} fill="#00BCD4" stroke="#00BCD4" />
                                            <div>
                                                <span className="text-[#00BCD4] dark:text-cyan-400 text-sm block">City of Fairburn</span>
                                                <span className="block text-gray-700 dark:text-gray-300">Fulton County, Georgia</span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Map */}
                                    <div className="h-full w-full">
                                        <MerchantHeatMap
                                            locations={data.locations}
                                            selectedLocation={null}
                                            onLocationSelect={() => { }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </Row>
                    </div>

                    <div className="min-h-[100px]"></div>

                    {/* Neighborhood Analysis Table */}
                    <Row className="w-full flex justify-center px-[20px] items-stretch">
                        <div style={{ maxWidth: "750px", width: "100%" }}>
                            <NeighborhoodTable data={data.neighborhood_analysis} />
                        </div>
                    </Row>
                </HeatMapPrintButton>
            )}
        </>
    );
}
